<template>
  <div class="details-tab" data-testid="setup-tasklist__details-tab">
    <div class="details-tab__section-label details-tab__section-label--required">Details</div>

    <CollapsibleTextAreaField
      :value="localForm.description"
      label="Task Name"
      variant="plain"
      :collapsible="false"
      :expanded="true"
      :required="true"
      :rows="5"
      :error="errors.description"
      dataTestId="setup-tasklist__template-description"
      @input="updateField('description', $event)"
    />

    <CollapsibleTextAreaField
      :value="localForm.purpose"
      label="Purpose"
      variant="card"
      :expanded.sync="purposeExpanded"
      :rows="3"
      dataTestId="setup-tasklist__template-purpose"
      @input="updateField('purpose', $event)"
    />

    <CollapsibleTextAreaField
      :value="localForm.instruction"
      label="Instruction"
      variant="card"
      :expanded.sync="instructionExpanded"
      :rows="3"
      dataTestId="setup-tasklist__template-instruction"
      @input="updateField('instruction', $event)"
    />

    <div class="details-tab__fields-block">
      <div class="details-tab__fields-grid">
        <div class="details-tab__grid-cell">
          <p class="details-tab__field-label details-tab__field-label--required">Category</p>
          <lb-dropdown
            :key="`category-${localForm._id || 'new'}-${localForm.category || ''}`"
            v-model="localForm.category"
            :items="categories"
            :customvalue="!isAR"
            :searchable="isAR"
            appendiconinner="mdi-chevron-down"
            :attach="false"
            :clearable="false"
            :error="errors.category"
            :hidedetails="!errors.category"
            label=""
            placeholder="Select Category"
            data-testid="setup-tasklist__template-category"
            @change="emitUpdate"
          />
        </div>
        <div class="details-tab__grid-cell">
          <p class="details-tab__field-label details-tab__field-label--required">Sub Category</p>
          <lb-dropdown
            :key="`subcategory-${localForm._id || 'new'}-${localForm.subcategory || ''}`"
            v-model="localForm.subcategory"
            :items="subcategories"
            :customvalue="!isAR"
            :searchable="isAR"
            appendiconinner="mdi-chevron-down"
            :attach="false"
            :clearable="false"
            :error="errors.subcategory"
            :hidedetails="!errors.subcategory"
            label=""
            placeholder="Select Sub Category"
            data-testid="setup-tasklist__template-subcategory"
            @change="emitUpdate"
          />
        </div>
        <div class="details-tab__grid-cell details-tab__evidence-cell">
          <p class="details-tab__field-label details-tab__evidence-count-label">
            {{ evidenceRequiredLabels.COUNT_LABEL }}
          </p>
          <div class="details-tab__evidence-row">
            <div class="details-tab__evidence-count">
              <v-text-field
                v-model.number="localForm.noofevidence"
                class="details-tab__evidence-input"
                type="number"
                :min="evidenceRequiredLabels.COUNT_MIN"
                :max="evidenceRequiredLabels.COUNT_MAX"
                :step="1"
                outlined
                dense
                hide-details="auto"
                :error-messages="errors.noofevidence"
                data-testid="setup-tasklist__template-no-of-evidences"
                @input="onEvidenceInput"
                @keypress="onEvidenceCountKeypress"
                @paste="onEvidenceCountPaste"
              />
            </div>
            <div class="details-tab__evidence-required-cell">
              <div class="details-tab__evidence-required-group">
                <p class="details-tab__evidence-required-label">
                  {{ evidenceRequiredLabels.LABEL }}
                </p>
                <button
                  type="button"
                  class="details-tab__evidence-toggle"
                  :class="{ 'details-tab__evidence-toggle--on': localForm.evidence_required }"
                  :aria-checked="localForm.evidence_required ? 'true' : 'false'"
                  :aria-label="evidenceRequiredLabels.LABEL"
                  data-testid="setup-tasklist__template-evidence-required-toggle"
                  @click="onEvidenceRequiredToggle"
                >
                  <span class="details-tab__evidence-toggle-thumb" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          v-for="field in resolvedCustomFields"
          :key="field.columnName || field._id"
          class="details-tab__grid-cell"
        >
          <p class="details-tab__field-label">{{ field.displayName || field.displayText || 'Custom Field' }}</p>
          <lb-string
            v-if="field.dataType === 'string'"
            v-model="localForm[field.columnName]"
            label=""
            :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
            :data-testid="`setup-tasklist__template-custom--${field.columnName}`"
            @input="emitUpdate"
          />
          <AddTaskDueDateField
            v-else-if="field.dataType === 'date'"
            v-model="localForm[field.columnName]"
            label=""
            :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
            :data-testid="`setup-tasklist__template-custom--${field.columnName}`"
            @input="emitUpdate"
          />
          <lb-number
            v-else-if="field.dataType === 'number'"
            v-model="localForm[field.columnName]"
            min="0"
            label=""
            :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
            :data-testid="`setup-tasklist__template-custom--${field.columnName}`"
            @input="emitUpdate"
          />
          <lb-string
            v-else
            v-model="localForm[field.columnName]"
            label=""
            :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
            :data-testid="`setup-tasklist__template-custom--${field.columnName}`"
            @input="emitUpdate"
          />
        </div>
      </div>
    </div>

    <div class="details-tab__schedule">
      <p class="details-tab__section-label details-tab__section-label--required">Schedule</p>

      <div>
        <p class="details-tab__field-label details-tab__field-label--required">Due Days</p>
        <v-text-field
          v-model="localForm.duedays"
          type="number"
          min="0"
          outlined
          dense
          hide-details
          placeholder="Enter due days"
          :error="!!errors.duedays"
          class="details-tab__due-days"
          data-testid="setup-tasklist__template-duedays"
          @input="emitUpdate"
        />
        <p
          v-if="errors.duedays"
          class="details-tab__field-error"
          role="alert"
          data-testid="setup-tasklist__template-duedays-error"
        >
          {{ errors.duedays }}
        </p>
      </div>

      <div class="details-tab__priority">
        <p class="details-tab__field-label details-tab__field-label--priority details-tab__field-label--required">Priority</p>
        <TaskPriorityDisplay
          variant="toggle-segment"
          :value="localForm.priority"
          :class="{ 'details-tab__priority--error': errors.priority }"
          data-testid="setup-tasklist__template-priority"
          @input="updateField('priority', $event)"
        />
        <p
          v-if="errors.priority"
          class="details-tab__field-error"
          role="alert"
        >
          {{ errors.priority }}
        </p>
      </div>

      <div class="details-tab__tags">
        <p
          id="setup-tasklist__template-frequency-label"
          class="details-tab__field-label details-tab__field-label--required"
        >
          Frequency
        </p>
        <lb-tagsInput
          :value="localForm.tags"
          class="details-tab__tags-input"
          tagcolor="purpleborder"
          :multiple="true"
          :multiplechips="true"
          :items="tagOptions"
          :hidedetails="true"
          :menu-search="true"
          :menu-offset-y="false"
          menu-content-class="details-tab__tags-menu"
          search-placeholder="Search"
          empty-message="No frequency found"
          appendiconinner="mdi-chevron-down"
          id="setup-tasklist__template-frequency"
          label=""
          placeholder=""
          aria-label="Select frequency"
          aria-labelledby="setup-tasklist__template-frequency-label"
          :error="errors.tags"
          data-testid="setup-tasklist__template-frequency"
          @input="updateField('tags', $event)"
        />
        <p
          v-if="errors.tags"
          class="details-tab__field-error"
          role="alert"
        >
          {{ errors.tags }}
        </p>
      </div>

      <div>
        <p class="details-tab__field-label details-tab__field-label--required">Assertions</p>
        <ChipMultiSelect
          :value="localForm.assertion"
          variant="add-task"
          :items="assertionOptions"
          :searchable="true"
          search-placeholder="Search"
          :error="errors.assertion"
          placeholder="Select assertions"
          empty-message="No assertions found"
          data-testid="setup-tasklist__template-assertion"
          data-testid-prefix="setup-tasklist__template-assertion"
          @input="updateField('assertion', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import './DetailsTab.scss';
import { isAccountReconciliation } from '../../utils/taskListHelpers';
import {
  ADD_TASK_FIELD_PLACEHOLDERS,
  resolveEvidenceFieldState,
  isEvidenceCountKeyAllowed,
  parsePastedEvidenceCount,
} from '../../../../Project/TaskList/drawers/AddTaskDialog/addTaskDialogUtils';
import AddTaskDueDateField from '../../../../Project/TaskList/drawers/AddTaskDialog/AddTaskDueDateField.vue';
import { ChipMultiSelect, CollapsibleTextAreaField } from '../../../../../components';
import TaskPriorityDisplay from '../../../../Project/TaskList/components/TaskPriorityDisplay/TaskPriorityDisplay.vue';
import { EVIDENCE_REQUIRED } from '../../../../../utils/constants';

function toOptionStrings(list = []) {
  const out = [];
  const seen = new Set();
  (list || []).forEach((item) => {
    const val =
      typeof item === 'string' || typeof item === 'number'
        ? String(item).trim()
        : String(item?.name || item?.label || item?.displayText || '').trim();
    if (!val) return;
    const key = val.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(val);
  });
  return out;
}

function uniqueCoaValues(coaList = [], field) {
  return toOptionStrings((coaList || []).map((row) => row?.[field]).filter(Boolean));
}

function optionsOrCoa(list, coaList, field) {
  const fromList = toOptionStrings(list);
  if (fromList.length) return fromList;
  return uniqueCoaValues(coaList, field);
}

export default {
  name: 'DetailsTab',

  components: {
    AddTaskDueDateField,
    ChipMultiSelect,
    CollapsibleTextAreaField,
    TaskPriorityDisplay,
  },

  props: {
    form: { type: Object, default: () => ({}) },
    errors: { type: Object, default: () => ({}) },
    formMeta: { type: Object, default: () => ({}) },
    chartOfAccounts: { type: Array, default: () => [] },
    customFields: { type: Array, default: () => [] },
  },

  data() {
    return {
      ADD_TASK_FIELD_PLACEHOLDERS,
      localForm: { ...(this.form || {}) },
      purposeExpanded: !!(this.form && this.form.purpose),
      instructionExpanded: !!(this.form && this.form.instruction),
      syncingFromProps: false,
    };
  },

  computed: {
    isAR() {
      return isAccountReconciliation(this.localForm.task_type, this);
    },
    categories() {
      if (this.isAR) {
        return optionsOrCoa(this.formMeta.coaCategoryAcc, this.chartOfAccounts, 'category');
      }
      const checklist = toOptionStrings([
        ...(this.formMeta.categoryAcc || []),
        ...(this.formMeta.categories || []),
      ]);
      if (checklist.length) return checklist;
      return uniqueCoaValues(this.chartOfAccounts, 'category');
    },
    subcategories() {
      if (this.isAR) {
        return optionsOrCoa(this.formMeta.coaSubcategoryAcc, this.chartOfAccounts, 'subcategory');
      }
      const checklist = toOptionStrings([
        ...(this.formMeta.subcategoryAcc || []),
        ...(this.formMeta.subcategories || []),
      ]);
      if (checklist.length) return checklist;
      return uniqueCoaValues(this.chartOfAccounts, 'subcategory');
    },
    tagOptions() {
      const masterDefaults = ['Yearly', 'Half-yearly', 'Monthly'];
      return toOptionStrings([...masterDefaults, ...(this.formMeta.tags || [])]);
    },
    assertionOptions() {
      return toOptionStrings([
        ...(this.$constants.TASK_LIST_CONSTANTS.ASSERTIONS || []),
        ...(this.formMeta.assertions || []),
      ]);
    },
    resolvedCustomFields() {
      return (this.customFields || []).filter((f) => !f.isSystem && f.columnName);
    },
    evidenceRequiredLabels() {
      return EVIDENCE_REQUIRED;
    },
  },

  watch: {
    form: {
      deep: true,
      immediate: true,
      handler(val) {
        this.syncingFromProps = true;
        this.localForm = {
          ...(val || {}),
          tags: Array.isArray(val?.tags) ? [...val.tags] : [],
          assertion: Array.isArray(val?.assertion) ? [...val.assertion] : [],
        };
        if (val?.purpose) this.purposeExpanded = true;
        if (val?.instruction) this.instructionExpanded = true;
        this.$nextTick(() => {
          this.syncingFromProps = false;
        });
      },
    },
  },

  methods: {
    updateField(key, val) {
      if (this.syncingFromProps) return;
      this.localForm = { ...this.localForm, [key]: val };
      this.emitUpdate();
    },
    emitUpdate() {
      if (this.syncingFromProps) return;
      this.$emit('update:form', { ...this.localForm });
    },

    onEvidenceRequiredToggle() {
      const evidence_required = !this.localForm.evidence_required;
      const next = resolveEvidenceFieldState({
        evidence_required,
        no_of_evidence: this.localForm.noofevidence,
      }, { fillDefaultWhenRequired: true, resetCountWhenNotRequired: true });
      this.localForm = {
        ...this.localForm,
        evidence_required,
        noofevidence: next.no_of_evidence,
      };
      this.emitUpdate();
      this.$emit('evidence-error', next.error);
    },

    onEvidenceInput(val) {
      const next = resolveEvidenceFieldState({
        evidence_required: this.localForm.evidence_required,
        no_of_evidence: val,
      });
      this.localForm = {
        ...this.localForm,
        noofevidence: next.no_of_evidence,
      };
      this.emitUpdate();
      this.$emit('evidence-error', next.error);
    },

    onEvidenceCountKeypress(e) {
      if (!isEvidenceCountKeyAllowed(e, this.localForm.noofevidence)) e.preventDefault();
    },

    onEvidenceCountPaste(e) {
      e.preventDefault();
      const pasted = parsePastedEvidenceCount(e.clipboardData && e.clipboardData.getData('text'));
      if (pasted == null) return;
      this.onEvidenceInput(pasted);
    },
  },
};
</script>
